"use client";
// Camión de lado con asientos: cada adulto o menor que se agrega "se sube" desde atrás y ocupa un lugar.
// Los lugares ya vendidos aparecen sentados en gris. Si hay más de un camión por salida, se llenan en orden.
import { useEffect, useRef, useState } from "react";

type Rol = "vendido" | "adulto" | "menor";

function Persona({ rol, entra }: { rol: Rol; entra: boolean }) {
  const cuerpo = rol === "vendido" ? "#8f9777" : rol === "adulto" ? "#ff7a1a" : "#2fa9df";
  const piel = rol === "vendido" ? "#b9bfa5" : "#f2c9a0";
  return (
    <svg viewBox="0 0 24 30" className={`pasajero ${rol}${entra ? " sube" : ""}`} aria-hidden="true">
      <path d="M3 30 C3 20 7 16 12 16 C17 16 21 20 21 30 Z" fill={cuerpo} />
      <circle cx="12" cy="9" r={rol === "menor" ? 6 : 6.8} fill={piel} />
      {rol === "adulto" && <path d="M5.2 8 C5.5 2 18.5 2 18.8 8 C15 5.6 9 5.6 5.2 8 Z" fill="#2b3414" />}
      {rol === "menor" && <circle cx="12" cy="2.6" r="2.6" fill="#ef5b8c" />}
    </svg>
  );
}

function Carroceria() {
  return (
    <svg className="camion-cuerpo" viewBox="0 0 360 150" aria-hidden="true">
      {/* techo y postes */}
      <path d="M22 18 H292 L318 30 H22 Z" fill="#1f9a52" />
      <path d="M22 30 H318" stroke="#13703a" strokeWidth="3" />
      {[30, 100, 170, 240, 300].map((x) => <rect key={x} x={x} y="30" width="5" height="64" fill="#13703a" />)}
      {/* cabina */}
      <path d="M292 30 H322 Q346 34 350 62 V100 H292 Z" fill="#ffc93c" />
      <path d="M300 38 H322 Q338 42 341 62 H300 Z" fill="#bfe7f5" />
      <circle cx="342" cy="88" r="5" fill="#fff6d0" />
      {/* caja */}
      <rect x="14" y="94" width="282" height="26" rx="6" fill="#2b3414" />
      <rect x="14" y="94" width="282" height="7" rx="3" fill="#ff7a1a" />
      <text x="156" y="113" textAnchor="middle" fontSize="12" fontWeight="800" fill="#ffc93c" fontFamily="var(--f-display), sans-serif" letterSpacing="2">SIERRA TOURS</text>
      {/* escalón trasero */}
      <rect x="2" y="108" width="14" height="6" rx="2" fill="#13703a" />
      {/* llantas */}
      {[70, 300].map((x) => (
        <g key={x} className="llanta" style={{ transformOrigin: `${x}px 128px` }}>
          <circle cx={x} cy="128" r="17" fill="#1b1f10" />
          <circle cx={x} cy="128" r="8" fill="#ffc93c" />
          <path d={`M${x - 8} 128 H${x + 8} M${x} 120 V136`} stroke="#2b3414" strokeWidth="2" />
        </g>
      ))}
    </svg>
  );
}

export function CamionLleno({ capacidad, vendidos, adultos, menores, camiones = 1 }: { capacidad: number; vendidos: number; adultos: number; menores: number; camiones?: number }) {
  const porCamion = Math.max(1, Math.ceil(capacidad / Math.max(1, camiones)));
  const roles: Rol[] = [
    ...Array<Rol>(Math.min(vendidos, capacidad)).fill("vendido"),
    ...Array<Rol>(adultos).fill("adulto"),
    ...Array<Rol>(menores).fill("menor"),
  ].slice(0, capacidad);

  // Solo animamos a quien se acaba de agregar: guardamos cuántos había en el render anterior.
  const previo = useRef(roles.length);
  const [recien, setRecien] = useState(0);
  useEffect(() => {
    const nuevos = Math.max(0, roles.length - previo.current);
    previo.current = roles.length;
    setRecien(nuevos);
    if (!nuevos) return;
    const id = setTimeout(() => setRecien(0), 900);
    return () => clearTimeout(id);
  }, [roles.length]);

  const vehiculos = Array.from({ length: Math.max(1, camiones) }, (_, v) => roles.slice(v * porCamion, (v + 1) * porCamion));
  const columnas = Math.ceil(porCamion / 2);

  return (
    <div className="camion-vivo" role="img" aria-label={`${roles.filter((r) => r !== "vendido").length} / ${capacidad}`}>
      {vehiculos.map((gente, v) => (
        <div key={v} className="camion-unidad">
          <Carroceria />
          <div className="camion-gente" style={{ ["--cols" as string]: columnas }}>
            {Array.from({ length: porCamion }, (_, i) => {
              const rol = gente[i];
              const fila = i % 2; // 0 fondo, 1 frente
              const col = Math.floor(i / 2);
              const esNuevo = !!rol && rol !== "vendido" && v * porCamion + i >= roles.length - recien;
              return (
                <span key={i} className={`lugar fila${fila}`} style={{ left: `${(col / columnas) * 100}%`, width: `${100 / columnas}%`, ["--retraso" as string]: `${Math.max(0, v * porCamion + i - (roles.length - recien)) * 90}ms` }}>
                  {rol ? <Persona key={rol + i} rol={rol} entra={esNuevo} /> : <i className="vacio" />}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
